import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Opportunity } from "../types";
import { submitFeedback } from "../api";
import ScoringBreakdown from "../components/ScoringBreakdown";

interface Props {
  opportunity: Opportunity;
  onClose: () => void;
}

function OpportunityDetail({ opportunity, onClose }: Props) {
  const opp = opportunity;
  const navigate = useNavigate();
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState<string | null>(null);

  const handleAction = async (action: "accept" | "reject") => {
    try {
      setSubmitting(true);
      setFeedbackMsg(null);
      await submitFeedback({
        opportunity_id: opp._id,
        action,
        reason: notes,
      });

      if (action === "accept") {
        // Navigate immediately to the combined workspace page
        navigate(`/workspace/${opp._id}`);
      } else {
        setFeedbackMsg("RFP Rejected.");
        setTimeout(onClose, 1800);
      }
    } catch (err: any) {
      setFeedbackMsg(err?.response?.data?.error || "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  const value = opp.estimated_value
    ? `$${Number(opp.estimated_value).toLocaleString()}`
    : "Not specified";

  const deadline = opp.response_deadline
    ? new Date(opp.response_deadline).toLocaleDateString("en-US")
    : "Not specified";

  const aiResult = opp.gate3_result || "N/A";
  const resultColor =
    aiResult === "QUALIFIED" ? "#27ae60"
    : aiResult === "MARGINAL" ? "#f39c12"
    : aiResult === "DISQUALIFIED" ? "#e74c3c"
    : "#95a5a6";

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.4)", zIndex: 1000,
        }}
      />

      {/* Drawer */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: "520px",
        backgroundColor: "white", zIndex: 1001, overflowY: "auto",
        boxShadow: "-4px 0 20px rgba(0,0,0,0.15)",
        fontFamily: "Segoe UI, sans-serif",
      }}>
        {/* Header */}
        <div style={{
          padding: "20px 24px", borderBottom: "1px solid #e0e0e0",
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        }}>
          <div style={{ flex: 1, marginRight: "16px" }}>
            <h2 style={{ margin: "0 0 8px", fontSize: "18px", color: "#2c3e50", lineHeight: 1.3 }}>
              {opp.title || "Untitled"}
            </h2>
            <div style={{
              display: "inline-block", padding: "3px 10px", borderRadius: "12px",
              fontSize: "11px", fontWeight: 600, color: resultColor,
              backgroundColor: `${resultColor}15`, border: `1px solid ${resultColor}30`,
            }}>
              {aiResult}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none", border: "none", fontSize: "24px",
              color: "#999", cursor: "pointer", padding: "0 4px",
            }}
          >
            &times;
          </button>
        </div>

        {/* Details */}
        <div style={{ padding: "20px 24px" }}>
          <table style={{ width: "100%", fontSize: "13px", borderCollapse: "collapse" }}>
            <tbody>
              {[
                ["Agency", opp.agency],
                ["Office", opp.office],
                ["NAICS", opp.naics_code],
                ["Set-Aside", opp.set_aside || "Unrestricted"],
                ["Value", value],
                ["Location", opp.place_of_performance || opp.state || "Not specified"],
                ["Deadline", deadline],
                ["Notice ID", opp.notice_id],
                ["Status", opp.status],
              ].map(([label, val]) => (
                <tr key={label as string}>
                  <td style={{ padding: "6px 12px 6px 0", color: "#999", fontWeight: 500, width: "100px" }}>
                    {label}
                  </td>
                  <td style={{ padding: "6px 0", color: "#2c3e50" }}>{val || "N/A"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {opp.url && (
            <a
              href={opp.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "inline-block", marginTop: "12px", fontSize: "13px", color: "#2980b9" }}
            >
              View Full Posting
            </a>
          )}
        </div>

        {/* Scoring Breakdown */}
        {opp.gate2_breakdown && opp.gate2_breakdown.length > 0 && (
          <div style={{ padding: "0 24px 20px", borderTop: "1px solid #f0f0f0", paddingTop: "20px" }}>
            <ScoringBreakdown breakdown={opp.gate2_breakdown} totalScore={opp.total_score} />
          </div>
        )}

        {/* AI Reasoning */}
        {opp.gate3_reasoning && (
          <div style={{
            margin: "0 24px 20px", padding: "14px", backgroundColor: "#f8f9fa",
            borderRadius: "6px", borderLeft: `3px solid ${resultColor}`,
          }}>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "#666", marginBottom: "6px" }}>
              AI Assessment
            </div>
            <p style={{ margin: 0, fontSize: "13px", color: "#2c3e50", lineHeight: 1.5 }}>
              {opp.gate3_reasoning}
            </p>
          </div>
        )}

        {/* Description */}
        {opp.description && (
          <div style={{ padding: "0 24px 20px" }}>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "#666", marginBottom: "6px" }}>
              Description
            </div>
            <p style={{
              margin: 0, fontSize: "13px", color: "#444", lineHeight: 1.6,
              maxHeight: "200px", overflow: "auto",
            }}>
              {opp.description}
            </p>
          </div>
        )}

        {/* Actions */}
        <div style={{
          padding: "20px 24px", borderTop: "1px solid #e0e0e0",
          position: "sticky", bottom: 0, backgroundColor: "white",
        }}>
          <div style={{ marginBottom: "12px" }}>
            <label style={{ fontSize: "12px", fontWeight: 600, color: "#666" }}>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this opportunity..."
              rows={3}
              style={{
                width: "100%", marginTop: "4px", padding: "10px", border: "1px solid #ddd",
                borderRadius: "6px", fontSize: "13px", resize: "vertical",
                fontFamily: "Segoe UI, sans-serif", boxSizing: "border-box",
              }}
            />
          </div>

          {feedbackMsg && (
            <div style={{
              padding: "10px", borderRadius: "4px", fontSize: "13px", marginBottom: "12px",
              backgroundColor: feedbackMsg.includes("Failed") ? "#fdecea" : "#d4edda",
              color: feedbackMsg.includes("Failed") ? "#c0392b" : "#155724",
            }}>
              {feedbackMsg}
            </div>
          )}

          <div style={{ display: "flex", gap: "12px" }}>
            <button
              onClick={() => handleAction("accept")}
              disabled={submitting}
              style={{
                flex: 1, padding: "12px", backgroundColor: "#27ae60", color: "white",
                border: "none", borderRadius: "6px", fontSize: "14px", fontWeight: 600,
                cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.6 : 1,
              }}
            >
              {submitting ? "Accepting..." : "Accept"}
            </button>
            <button
              onClick={() => handleAction("reject")}
              disabled={submitting}
              style={{
                flex: 1, padding: "12px", backgroundColor: "#e74c3c", color: "white",
                border: "none", borderRadius: "6px", fontSize: "14px", fontWeight: 600,
                cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.6 : 1,
              }}
            >
              {submitting ? "Rejecting..." : "Reject"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default OpportunityDetail;
